// src/features/bookings/components/AsyncCityAutoComplete.jsx
import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
  Typography,
  InputAdornment,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { autocompleteCities, getPlaceLocation, reverseGeocode } from '../api/api';

export default function AsyncCityAutocomplete({
  label = 'Where',
  value,
  onChange,
  sessionToken,
  country,
  size = 'medium',
  minWidth = { xs: 140, sm: 200, md: 260 }, // ⬅ smaller default + customizable
}) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const mountedRef = useRef(false);
  const restoredRef = useRef(false);

  // Geolocation state
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState(null);

  const handleUseMyLocation = async () => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser.');
      return;
    }
    setLocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const place = await reverseGeocode({ lat: latitude, lon: longitude });
          onChange(place);
          setInput(place.label || '');
        } catch {
          setGeoError('Could not determine your location. Please search manually.');
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError('Location access denied. Please enable it in your browser settings.');
        } else if (err.code === err.TIMEOUT) {
          setGeoError('Location request timed out. Please try again.');
        } else {
          setGeoError('Could not determine your location. Please search manually.');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const needle = useMemo(() => (input || '').trim().toLowerCase(), [input]);

  // Sync input with controlled value when provided, without triggering fetch on first paint
  useEffect(() => {
    if (!value?.label) return;
    setInput(value.label);
    if (!mountedRef.current) {
      restoredRef.current = true; // skip first fetch after hydrate
    }
  }, [value]);

  useEffect(() => {
    if (!input || input.length < 2) {
      setOptions([]);
      return;
    }
    if (restoredRef.current && !mountedRef.current) {
      // Initial hydrate: don't auto-fetch suggestions
      mountedRef.current = true;
      restoredRef.current = false;
      return;
    }
    mountedRef.current = true;
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await autocompleteCities({ input, sessionToken, country, signal: ctrl.signal });
        setOptions(res);
      } finally {
        setLoading(false);
      }
    }, 80);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [input, sessionToken, country]);

  const handleSelect = async (_, opt) => {
    if (!opt) { onChange(null); return; }
    try {
      const { lat, lon } = await getPlaceLocation({ placeId: opt.placeId, sessionToken });
      onChange({ ...opt, lat, lon });
    } catch {
      onChange(opt);
    }
  };

  const highlight = (text) => {
    const n = needle;
    if (!n) return text;
    const i = text.toLowerCase().indexOf(n);
    if (i === -1) return text;
    return (<>{text.slice(0,i)}<strong>{text.slice(i,i+n.length)}</strong>{text.slice(i+n.length)}</>);
  };

  return (
    <>
    <Autocomplete
      options={options}
      value={value}
      onChange={handleSelect}
      onInputChange={(_, v) => setInput(v)}
      autoHighlight
      getOptionLabel={(o) => o?.label || ''}
      sx={{
        flex: 1,
        minWidth, // ⬅ uses the prop
        '& .MuiOutlinedInput-root': {
          borderRadius: 2.5,
          transition: 'box-shadow .2s ease',
          '&.Mui-focused': {
            boxShadow: (t) =>
              t.palette.mode === 'dark'
                ? '0 0 0 2px rgba(144,202,249,.25)'
                : '0 0 0 3px rgba(25,118,210,.12)',
          },
        },
        '& .MuiAutocomplete-paper': { borderRadius: 12, boxShadow: (t) => t.shadows[6] },
        '& .MuiAutocomplete-listbox': { pt: 0.75, pb: 0.75, maxHeight: 360 },
        '& .MuiAutocomplete-option': { py: 1.05 },
      }}
      slotProps={{
        popupIndicator: {
          variant: 'plain',
          sx: {
            color: 'text.secondary',
            p: 0.75,
            mr: 0.25,
          },
        },
        clearIndicator: {
          variant: 'plain',
          sx: {
            color: 'text.secondary',
            p: 0.75,
            mr: 0.25,
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search city"
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ color: 'text.secondary', pl: 0.25, gap: 0.25 }}>
                <PlaceIcon
                  sx={{
                    fontSize: 18,
                    width: 18,
                    height: 18,
                    transform: 'none',
                  }}
                />
                <Tooltip title="Use my current location" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleUseMyLocation}
                      disabled={locating}
                      sx={{
                        color: locating ? 'primary.main' : 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                        p: 0.35,
                      }}
                    >
                      {locating
                        ? <CircularProgress size={16} />
                        : <MyLocationIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, opt) => (
        <li {...props} key={opt.placeId}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <Box
              sx={{
                width: 26, height: 26, borderRadius: '50%',
                bgcolor: 'action.hover', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: 'text.secondary', flex: '0 0 auto',
              }}
            >
              <PlaceIcon fontSize="small" />
            </Box>
            <Stack direction="column" sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} noWrap>
                {highlight(opt.city || opt.label)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {opt.state ? `${opt.state}, ` : ''}{opt.country}
              </Typography>
            </Stack>
          </Stack>
        </li>
      )}
      noOptionsText={input.length < 2 ? 'Type at least 2 characters' : 'No matches'}
      clearOnBlur={false}
      openOnFocus
    />
    <Snackbar
      open={!!geoError}
      autoHideDuration={5000}
      onClose={() => setGeoError(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={() => setGeoError(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
        {geoError}
      </Alert>
    </Snackbar>
    </>
  );
}
